// /src/components/imovel/MegaFilterSidebar.jsx
// VERSÃO 9.6 SUPREMA - TIM UNITY DYNAMIC RADAR (CORREÇÃO DE PÚBLICO ALVO)

import React, { useState, useEffect, useCallback } from 'react';
import { 
    FiFilter, FiChevronLeft, FiChevronRight, FiSearch, 
    FiDollarSign, FiMapPin, FiZap, FiRefreshCcw, FiUsers, FiBriefcase
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../../api/apiClient';

export default function MegaFilterSidebar({ onFilter, initialFilters }) {
    const [isOpen, setIsOpen] = useState(true);
    const [localFilters, setLocalFilters] = useState(initialFilters);
    const [dbOptions, setDbOptions] = useState({ cidades: [], estados: [] }); 

    // 📡 CARGA DE INTELIGÊNCIA GEOGRÁFICA
    useEffect(() => {
        apiClient.get('/api/filtros/options')
            .then(res => setDbOptions(res.data))
            .catch(err => console.error("Falha ao varrer opções de abrangência:", err));
    }, []);

    // 🚀 DISPARO REATIVO (INSTANT SEARCH)
    const triggerFilter = useCallback((updatedFilters) => {
        // Limpamos campos vazios e "Ambos" antes de enviar para o pai
        const cleanFilters = { ...updatedFilters };
        if (cleanFilters.tipoPrincipal === 'Ambos') cleanFilters.tipoPrincipal = '';
        
        onFilter(cleanFilters);
    }, [onFilter]);

    // 🎯 HANDLER DE MUDANÇA ADAPTATIVO
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        
        const newFilters = { ...localFilters, [name]: val };
        setLocalFilters(newFilters);

        // Disparo automático para filtros de seleção rápida
        if (name === 'nivelCobertura' || name === 'cidade' || name === 'estado' || type === 'checkbox') {
            triggerFilter(newFilters);
        }
    };

    // ⚡ COMANDO DE PÚBLICO (CPF / CNPJ / AMBOS)
    const handlePublicoChange = (tipo) => {
        const newFilters = { ...localFilters, tipoPrincipal: tipo };
        setLocalFilters(newFilters);
        triggerFilter(newFilters);
    };

    // 🧹 LIMPEZA DE RADAR (RESET TOTAL)
    const clearFilters = () => {
        const reset = {
            tipoPrincipal: 'Ambos', // Deixamos "Ambos" visualmente, mas o triggerFilter converte para "" (vazio)
            nivelCobertura: '',
            cidade: '',
            estado: '',
            minPreco: '',
            maxPreco: '',
            isDestaque: false
        };
        setLocalFilters(reset);
        triggerFilter(reset); // Mudança tática aqui: chama o trigger que limpa o 'Ambos'
    };

    return (
        <div className="relative flex h-full transition-all duration-500">
            {/* GATILHO DE COMANDO (HUD FLOATING) */}
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="absolute -right-4 top-10 z-50 p-2 rounded-full shadow-2xl transition-all hover:scale-110 active:scale-90 border-4"
                style={{ 
                    backgroundColor: 'var(--accent-color)', 
                    color: '#000',
                    borderColor: 'var(--bg-main)' 
                }}
            >
                {isOpen ? <FiChevronLeft size={20} /> : <FiChevronRight size={20} />}
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div 
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: 320, opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        className="border-r h-[calc(100vh-100px)] overflow-y-auto custom-scrollbar shadow-2xl relative transition-all duration-500"
                        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
                    >
                        <div className="p-8 space-y-10">
                            
                            {/* HEADER COM RESET TÁTICO (H3 NORMALIZADO) */}
                            <div className="flex items-center justify-between border-b border-auto pb-6">
                                <div className="flex items-center gap-3">
                                    <FiFilter className="text-accent" size={20} />
                                    <h3 className="text-auto leading-none">Filtros TIM</h3>
                                </div>
                                <button 
                                    onClick={clearFilters} 
                                    className="p-2 rounded-xl bg-accent/5 text-accent hover:bg-accent/20 transition-all group flex items-center gap-2"
                                    title="Resetar Radar"
                                >
                                    <FiRefreshCcw size={14} className="group-hover:rotate-180 transition-transform duration-700" />
                                    <span className="text-[9px] font-black uppercase tracking-widest">Reset</span>
                                </button>
                            </div>

                            {/* SEÇÃO 1: PÚBLICO ALVO (CPF / CNPJ) */}
                            <div className="space-y-4">
                                <span className="text-[10px] font-black uppercase tracking-[0.4em] text-sec-auto opacity-40 ml-1">Público Alvo</span>
                                <div className="grid grid-cols-1 gap-2.5">
                                    {[
                                        { id: 'CPF', label: 'Pessoa Física', icon: FiUsers },
                                        { id: 'CNPJ', label: 'Corporativo', icon: FiBriefcase },
                                        { id: 'Ambos', label: 'Todos os Públicos', icon: FiZap }
                                    ].map(t => {
                                        const isActive = localFilters.tipoPrincipal === t.id;
                                        return (
                                            <button 
                                                key={t.id}
                                                type="button"
                                                onClick={() => handlePublicoChange(t.id)}
                                                className={`py-4 px-4 text-[10px] font-black rounded-2xl uppercase transition-all border flex items-center gap-3 ${
                                                    isActive ? 'shadow-xl scale-[1.02]' : 'opacity-40 hover:opacity-100'
                                                }`}
                                                style={{ 
                                                    backgroundColor: isActive ? 'var(--accent-color)' : 'var(--bg-main)',
                                                    borderColor: isActive ? 'var(--accent-color)' : 'var(--border-color)',
                                                    color: isActive ? '#000' : 'var(--text-primary)'
                                                }}
                                            >
                                                <t.icon size={14} /> {t.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* SEÇÃO 2: ABRANGÊNCIA GEOGRÁFICA */}
                            <div className="space-y-6">
                                <span className="text-[10px] font-black uppercase tracking-[0.4em] text-sec-auto opacity-40 ml-1">Cobertura</span>
                                
                                <div className="space-y-2">
                                    <select 
                                        name="nivelCobertura" 
                                        value={localFilters.nivelCobertura || ''} 
                                        onChange={handleChange}
                                        className="w-full rounded-2xl text-xs font-black p-4 outline-none border border-auto transition-all cursor-pointer shadow-inner appearance-none"
                                        style={{ backgroundColor: 'var(--bg-main)', color: 'var(--text-primary)' }}
                                    >
                                        <option value="" className="text-sec-auto">🌐 Qualquer Abrangência</option>
                                        <option value="Brasil">🇧🇷 Nível Brasil (Nacional)</option>
                                        <option value="Estados">🗺️ Nível Estadual (Regiões)</option>
                                        <option value="Cidades">📍 Nível Municipal (Local)</option>
                                    </select>
                                </div>

                                {/* Mostra Filtro de Estado se Nível for Estado ou Cidade */}
                                {(localFilters.nivelCobertura === 'Estados' || localFilters.nivelCobertura === 'Cidades') && (
                                    <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                                        <select 
                                            name="estado" 
                                            value={localFilters.estado || ''} 
                                            onChange={handleChange}
                                            className="w-full rounded-2xl text-xs font-black p-4 outline-none border border-auto transition-all cursor-pointer shadow-inner appearance-none"
                                            style={{ backgroundColor: 'var(--bg-main)', color: 'var(--text-primary)' }}
                                        >
                                            <option value="" className="text-sec-auto">Selecione o Estado</option>
                                            {dbOptions.estados?.map(e => <option key={e} value={e}>{e}</option>)}
                                        </select>
                                    </div>
                                )}

                                {/* Mostra Filtro de Cidade só se Nível for Cidade */}
                                {localFilters.nivelCobertura === 'Cidades' && (
                                    <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                                        <select 
                                            name="cidade" 
                                            value={localFilters.cidade || ''} 
                                            onChange={handleChange}
                                            className="w-full rounded-2xl text-xs font-black p-4 outline-none border border-auto transition-all cursor-pointer shadow-inner appearance-none"
                                            style={{ backgroundColor: 'var(--bg-main)', color: 'var(--text-primary)' }}
                                        >
                                            <option value="" className="text-sec-auto">Selecione a Cidade</option>
                                            {dbOptions.cidades?.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {/* SEÇÃO 3: FAIXA DE INVESTIMENTO */}
                            <div className="space-y-4 border-t border-auto pt-8">
                                <div className="flex items-center gap-3 text-accent mb-2">
                                    <FiDollarSign size={14} />
                                    <span className="text-[10px] font-black uppercase tracking-[0.3em]">Assinatura Mensal</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <input type="number" name="minPreco" value={localFilters.minPreco} onChange={handleChange} placeholder="Mín." 
                                               className="w-full rounded-xl p-4 border border-auto text-xs font-black outline-none focus:border-accent transition-all shadow-inner"
                                               style={{ backgroundColor: 'var(--bg-main)', color: 'var(--text-primary)' }} />
                                    </div>
                                    <div className="space-y-1">
                                        <input type="number" name="maxPreco" value={localFilters.maxPreco} onChange={handleChange} placeholder="Máx." 
                                               className="w-full rounded-xl p-4 border border-auto text-xs font-black outline-none focus:border-accent transition-all shadow-inner"
                                               style={{ backgroundColor: 'var(--bg-main)', color: 'var(--text-primary)' }} />
                                    </div>
                                </div>
                            </div>

                            {/* SEÇÃO 4: SELEÇÃO ELITE */}
                            <div className="pt-2">
                                <label className="flex items-center gap-4 cursor-pointer group p-3 rounded-2xl border border-auto transition-all hover:bg-accent/5"
                                       style={{ backgroundColor: 'var(--bg-main)' }}>
                                    <div className="relative flex items-center justify-center">
                                        <input 
                                            type="checkbox" name="isDestaque" checked={localFilters.isDestaque || false} onChange={handleChange} 
                                            className="peer appearance-none w-7 h-7 rounded-xl border border-auto transition-all cursor-pointer"
                                            style={{ backgroundColor: 'var(--bg-card)' }}
                                        />
                                        <FiZap className="absolute text-accent opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" size={14} />
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-auto opacity-40 group-hover:opacity-100 transition-opacity">
                                        Apenas Ofertas em Destaque
                                    </span>
                                </label>
                            </div>

                            {/* BOTÃO DE REFORÇO (COM GLOW DINÂMICO) */}
                            <button 
                                onClick={() => triggerFilter(localFilters)}
                                className="w-full py-5 font-black uppercase tracking-[0.4em] text-[11px] rounded-2xl shadow-2xl transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-4 mt-8"
                                style={{ 
                                    backgroundColor: 'var(--accent-color)', 
                                    color: '#000',
                                    boxShadow: '0 10px 30px -10px var(--accent-color)'
                                }}
                            >
                                <FiSearch /> Disparar Radar
                            </button>

                            <p className="text-center text-[8px] font-black uppercase tracking-[0.5em] opacity-20 text-auto pt-4">
                                Sniper Search Unit v9.6
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}